import React, { useCallback, useRef, useState } from "react";
import {
  Loader2,
  Check,
  AlertTriangle,
  Type,
  FileType2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { CloudUploadIcon, RefreshCWIcon } from "../../../components/icons";
import CfLoader from "../../CfLoader/CfLoader";
import { CARD_CLASS } from "../utils/styles";
import Toast from "../../Toast/Toast";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function FontChip({ name, variants, resolved = true, t }) {
  return (
    <div
      className={`group relative inline-flex flex-col gap-1.5 px-4 py-3 rounded-xl border-2 transition-all duration-300 ${resolved
        ? "border-cfActiveChipBorder bg-cfActiveSubtleBg hover:border-cfActiveChipBorderHover hover:shadow-sm"
        : "border-amber-400/40 bg-amber-50 hover:border-amber-400/60 hover:shadow-sm"
        }`}
    >
      <div className="flex items-center gap-2">
        {resolved ? (
          <div className="w-5 h-5 rounded-full bg-cfActiveLightBg flex items-center justify-center shrink-0">
            <Check
              className="w-3 h-3 text-cfActiveText"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        ) : (
          <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle
              className="w-3 h-3 text-amber-600"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        )}
        <span
          className={`text-sm font-semibold ${resolved ? "text-cfActiveText" : "text-amber-700"
            }`}
        >
          {name}
        </span>
      </div>
      {variants && variants.length > 0 && (
        <div className="flex flex-wrap gap-1 ml-7">
          {variants.map((v, i) => (
            <span
              key={i}
              className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${resolved
                ? "bg-cfActiveLightBg text-cfActiveMutedText border border-cfActiveLightBorder"
                : "bg-amber-100/80 text-amber-600/80 border border-amber-300/30"
                }`}
            >
              {t("certificateOnboarding:font_prep_weight", {
                weight: v.weight,
              })}{" "}
              ·{" "}
              {t("certificateOnboarding:font_prep_style", {
                style: v.style,
              })}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FontPreparationSection({
  fontPrepData,
  isFontPrepLoading,
  fontPrepError,
  onPrepareFonts,
  manualFontCss,
  isManualFontUploading,
  onManualFontUpload,
  onManualFontRemove,
  disabled = false,
  t,
}) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const hasPrepared = fontPrepData !== null;
  const isUploadLocked = disabled || !hasPrepared || isManualFontUploading;
  const resolvedFonts = fontPrepData?.resolved_fonts || [];
  const missingFonts = fontPrepData?.missing_fonts || [];
  const requiredFonts = fontPrepData?.required_fonts || {};
  const hasMissingFonts = missingFonts.length > 0;

  const handleDetectClick = useCallback(() => {
    onPrepareFonts();
  }, [onPrepareFonts]);

  const validateAndUpload = useCallback(
    (file, inputElement) => {
      if (!file) return;

      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      if (extension !== "css") {
        if (inputElement) inputElement.value = "";
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:invalid_file_type"),
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        if (inputElement) inputElement.value = "";
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:file_size_limit"),
        });
        return;
      }

      if (inputElement) inputElement.value = "";
      onManualFontUpload(file);
    },
    [onManualFontUpload, t],
  );

  const handleFileChange = (event) => {
    if (isUploadLocked) return;
    const file = event.target.files[0];
    validateAndUpload(file, event.target);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploadLocked) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (isUploadLocked) return;
    const file = event.dataTransfer.files[0];
    validateAndUpload(file, fileInputRef.current);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isUploadLocked) fileInputRef.current?.click();
    }
  };

  const handleRemove = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    onManualFontRemove();
  };

  return (
    <section
      id="cert-onboarding-font-preparation"
      className={`${CARD_CLASS} p-4 sm:p-6 mt-4 transition-all duration-300 ${disabled ? "opacity-70 pointer-events-none cursor-not-allowed" : ""}`}
      aria-labelledby="font-prep-heading"
    >
      <div className="mb-4 pb-4 border-b border-cfCardBorder">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-cfPrimary flex items-center justify-center">
              <Type className="w-4 h-4 text-cfSecondary" strokeWidth={2} aria-hidden />
            </div>
            <div>
              <h2
                id="font-prep-heading"
                className="text-base font-semibold text-cfHeadingText leading-tight"
              >
                {t("certificateOnboarding:font_prep_section_title")}
              </h2>
              <p className="text-xs text-cfGlobalText mt-0.5">
                {t("certificateOnboarding:font_prep_section_subtitle")}
              </p>
            </div>
          </div>

          {hasPrepared && (
            <button
              type="button"
              id="cert-font-redetect-btn"
              onClick={handleDetectClick}
              disabled={isFontPrepLoading}
              title={t("certificateOnboarding:font_prep_redetect_button")}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-cfCardBorder text-cfGlobalText bg-cfSecondary hover:bg-cfCardHover hover:border-cfPrimary/30 hover:text-cfPrimary transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-busy={isFontPrepLoading}
            >
              {isFontPrepLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCWIcon size={14} className="inline-flex" aria-hidden />
              )}
              <span className="hidden sm:inline">
                {t("certificateOnboarding:font_prep_redetect_button")}
              </span>
            </button>
          )}
        </div>
      </div>
      <div>

        {fontPrepError && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-cfStatusBlockedText/5 border border-cfStatusBlockedText/20">
            <AlertCircle
              className="w-4 h-4 text-cfStatusBlockedText shrink-0"
              aria-hidden
            />
            <p className="text-sm text-cfStatusBlockedText">
              {t("certificateOnboarding:font_prep_failed")}
            </p>
          </div>
        )}

        {isFontPrepLoading && (
          <div className="mb-4">
            <p className="text-xs font-medium text-cfGlobalText mb-3 uppercase tracking-wider">
              {t("certificateOnboarding:font_prep_detecting")}
            </p>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-xl bg-cfCardHover animate-pulse"
                  style={{ width: `${120 + Math.random() * 60}px` }}
                />
              ))}
            </div>
          </div>
        )}

        {hasPrepared && !isFontPrepLoading && resolvedFonts.length > 0 && (
          <div className="mb-4" role="group" aria-label={t("certificateOnboarding:font_prep_resolved_title")}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2
                className="w-4 h-4 text-cfActiveText"
                strokeWidth={2}
                aria-hidden
              />
              <p className="text-xs font-medium text-cfActiveText uppercase tracking-wider">
                {t("certificateOnboarding:font_prep_resolved_title")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {resolvedFonts.map((fontName) => (
                <FontChip
                  key={fontName}
                  name={fontName}
                  variants={requiredFonts[fontName] || []}
                  resolved
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        {hasPrepared && !isFontPrepLoading && hasMissingFonts && (
          <div
            className="mb-5 p-4 rounded-xl border-2 border-amber-300/40 bg-amber-50/50"
            role="group"
            aria-label={t("certificateOnboarding:font_prep_missing_title")}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle
                className="w-4 h-4 text-amber-600"
                strokeWidth={2}
                aria-hidden
              />
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
                {t("certificateOnboarding:font_prep_missing_title")}
              </p>
            </div>
            <p className="text-sm text-amber-700/80 mb-4 leading-relaxed">
              {t("certificateOnboarding:font_prep_missing_hint")}
            </p>
            <div className="flex flex-wrap gap-3">
              {missingFonts.map((fontName) => (
                <FontChip
                  key={fontName}
                  name={fontName}
                  variants={[]}
                  resolved={false}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        <div className={`${!hasPrepared ? "opacity-70" : ""}`}>
          <div className="mb-3 flex items-start gap-3">
            <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-cfPrimary text-cfSecondary text-xs font-semibold shrink-0">
              {hasPrepared ? "03" : "03"}
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-cfHeadingText">
                {t("certificateOnboarding:font_prep_optional_css_label")}
              </h3>
              <p className="text-xs text-cfGlobalText mt-0.5">
                {t("certificateOnboarding:font_prep_optional_css_helper")}
              </p>
            </div>
          </div>

          {manualFontCss && !isManualFontUploading ? (
            <div
              className={`relative rounded-lg border p-3 bg-cfSecondary ${hasMissingFonts
                ? "border-amber-300/50 border-l-4 border-l-amber-400"
                : "border-cfCardBorder border-l-4 border-l-cfPrimary"
                }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-cfHeadingText truncate pr-2">
                  {manualFontCss.file_name}
                </span>
                <button
                  type="button"
                  id="cert-font-manual-css-remove"
                  onClick={handleRemove}
                  className="shrink-0 text-cfStatusBlockedText hover:opacity-80 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary rounded-sm"
                >
                  {t("certificateOnboarding:remove")}
                </button>
              </div>
              <div className="flex items-center gap-2 text-cfGlobalText">
                <FileType2
                  className="w-5 h-5 text-cfPrimary shrink-0"
                />
                <span className="text-xs">
                  {t("certificateOnboarding:css_format_hint")}
                </span>
              </div>
            </div>
          ) : (
            <div
              className={`flex gap-4 py-6 px-4 cursor-pointer text-cfGlobalText rounded-lg outline-dotted outline-offset-2 shadow-sm transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${hasMissingFonts
                ? "outline-amber-300/60 bg-amber-50/30 hover:bg-amber-50/50"
                : "outline-cfCardBorder bg-cfSecondary/50 hover:bg-cfSecondary"
                } ${dragActive ? "bg-cfSecondary" : ""} ${isUploadLocked ? "pointer-events-none opacity-70" : ""
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={isUploadLocked ? -1 : 0}
              aria-disabled={isUploadLocked}
              aria-label={t("certificateOnboarding:font_prep_optional_css_label")}
            >
              <input
                type="file"
                accept=".css"
                onChange={handleFileChange}
                className="hidden"
                id="cert-onboarding-manual-font-css"
                ref={fileInputRef}
                disabled={isUploadLocked}
              />
              <label
                htmlFor="cert-onboarding-manual-font-css"
                className={`cursor-pointer flex flex-row w-full ${isManualFontUploading ? "cursor-wait" : ""
                  }`}
              >
                {isManualFontUploading ? (
                  <div className="flex items-center gap-3 w-full justify-center py-2">
                    <div className="scale-50 origin-center">
                      <CfLoader />
                    </div>
                    <span className="text-sm text-cfGlobalText">
                      {t("certificateOnboarding:uploading")}
                    </span>
                  </div>
                ) : (
                  <>
                    <CloudUploadIcon
                      size={36}
                      className={`shrink-0 my-auto mr-4 inline-flex ${hasMissingFonts ? "text-amber-500" : "text-cfPrimary"}`}
                      aria-hidden
                    />
                    <div className="flex flex-col">
                      <div className="text-sm">
                        <span
                          className={`font-semibold ${hasMissingFonts ? "text-amber-600" : "text-cfPrimary"
                            }`}
                        >
                          {t("certificateOnboarding:upload_click")}{" "}
                        </span>
                        <span className="text-cfGlobalText">
                          {t("certificateOnboarding:upload_drag")}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-cfGlobalText">
                        {t("certificateOnboarding:css_format_hint")}
                      </div>
                    </div>
                  </>
                )}
              </label>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default FontPreparationSection;
